import React, { useState } from 'react';
import { LabelingMethodSelector } from './label-selection/LabelingMethodSelector';
import { DisabledLabelView } from './label-selection/DisabledLabelView';

interface ColumnInfo {
  name: string;
  type: string;
}

interface LabelSelectionProps {
  columns: ColumnInfo[];
  labelColumn: string;
  availableLabels: string[];
  positiveLabels: string[];
  negativeLabels: string[];
  onLabelColumnChange: (column: string) => void;
  onPositiveLabelsChange: (labels: string[]) => void;
  onNegativeLabelsChange: (labels: string[]) => void;
  disabled?: boolean;
}

type LabelingMethod = 'existing-column' | 'criteria-selection' | 'swipe-cards';
type LabelClassification = 'positive' | 'negative' | 'unknown';

interface LabelValue {
  value: string;
  count: number;
  classification: LabelClassification;
}

export const LabelSelection: React.FC<LabelSelectionProps> = ({
  columns,
  labelColumn,
  availableLabels,
  positiveLabels,
  negativeLabels,
  onLabelColumnChange,
  onPositiveLabelsChange,
  onNegativeLabelsChange,
  disabled
}) => {
  const [labelingMethod, setLabelingMethod] = useState<LabelingMethod>('existing-column');
  const [labelValues, setLabelValues] = useState<LabelValue[]>([]);

  // Mock function to generate label values when a column is selected
  const generateLabelValues = (column: string): LabelValue[] => {
    const mockValues = ['B2B', 'B2C', 'Enterprise', 'SMB', 'Startup', 'Fortune 500', 'Mid-market'];
    return mockValues.map(value => ({
      value,
      count: Math.floor(Math.random() * 500) + 50,
      classification: 'unknown' as LabelClassification
    }));
  };

  const handleLabelColumnChange = (column: string) => {
    onLabelColumnChange(column);
    const newLabelValues = generateLabelValues(column);
    setLabelValues(newLabelValues);
  };

  const handleClassificationChange = (index: number, classification: LabelClassification) => {
    const newLabelValues = [...labelValues];
    newLabelValues[index].classification = classification;
    setLabelValues(newLabelValues);

    // Update positive and negative labels based on classifications
    const positives = newLabelValues
      .filter(lv => lv.classification === 'positive')
      .map(lv => lv.value);
    const negatives = newLabelValues
      .filter(lv => lv.classification === 'negative')
      .map(lv => lv.value);
    
    onPositiveLabelsChange(positives);
    onNegativeLabelsChange(negatives);
  };

  if (disabled) {
    return (
      <DisabledLabelView
        labelColumn={labelColumn}
        positiveLabels={positiveLabels}
        negativeLabels={negativeLabels}
      />
    );
  }

  return (
    <div className="space-y-6">
      <LabelingMethodSelector
        labelingMethod={labelingMethod}
        onMethodChange={setLabelingMethod}
        columns={columns}
        labelColumn={labelColumn}
        onLabelColumnChange={handleLabelColumnChange}
        labelValues={labelValues}
        onClassificationChange={handleClassificationChange}
      />
    </div>
  );
}; 